import {Component, computed, inject, input, linkedSignal, model, output} from '@angular/core';

import {FraisFixeDTO} from '../../../../core/dtos/frais-fixe-dto';
import {FraisFixeService} from '../../../../core/services/entities/frais-fixe.service';
import {ToastService} from '../../../../core/services/technique/toast.service';
import {Auth2Service} from '../../../../core/services/security/auth2.service';
import {TaxesService} from '../../../../core/services/entities/taxes.service';
import {SiteDTO} from '../../../../core/dtos/site-dto';
import {TaxeDTO} from '../../../../core/dtos/taxe-dto';
import {MSG_KEY, MSG_SEVERITY, TYPE_FRAIS_FIXE, TYPE_FRAIS_FIXE_LABELS} from '../../../../core/constants';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';

@Component({
  selector: 'yo-fraisFixeEditionDialog',
  templateUrl: './frais-fixe-edition-dialog.component.html',
  styleUrls: ['./frais-fixe-edition-dialog.component.scss'],
  standalone: false
})
export class FraisFixeEditionDialogComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;

  private readonly fraisFixeSvc = inject(FraisFixeService);
  private readonly toastSvc = inject(ToastService);
  private readonly auth2Svc = inject(Auth2Service);
  readonly taxesSvc = inject(TaxesService);

  readonly displayDialog = model<boolean>(false);
  readonly dto = input<FraisFixeDTO | null>(null);
  readonly saved = output<{ dto: FraisFixeDTO; isUpdate: boolean }>();

  readonly typesFraisFixe = Object.values(TYPE_FRAIS_FIXE).map(value => ({
    value,
    label: TYPE_FRAIS_FIXE_LABELS[value]
  }));

  readonly dialogTitle = computed(() => this.dto()?.id
    ? 'Modification d\'un frais fixe'
    : 'Création d\'un frais fixe'
  );

  readonly formData = linkedSignal(() => {
    const dto = this.dto();
    return {
      id: dto?.id ?? null,
      libelle: dto?.libelle ?? '',
      code: dto?.code ?? '',
      site: dto?.site ?? null,
      taxe: dto?.taxe ?? null,
      typeFraisFixe: dto?.typeFraisFixe ?? null
    };
  });

  readonly isFormValid = computed(() => {
    const d = this.formData();
    return !!d.site?.id
      && !!d.taxe?.id
      && !!d.typeFraisFixe
      && !!d.libelle?.trim() && d.libelle.length <= 100
      && !!d.code?.trim() && d.code.length <= 30;
  });

  readonly canModify = computed(() => this.fraisFixeSvc.canModify(this.dto()));

  readonly sitePlaceholder = computed(() => !this.canModify()
    ? this.dto()?.site?.libelle ?? ''
    : 'Sélectionner le site...'
  );

  readonly taxePlaceholder = computed(() => !this.canModify()
    ? this.dto()?.taxe?.libelle ?? ''
    : 'Sélectionner la taxe...'
  );

  findAllLocalSites = (): SiteDTO[] => this.auth2Svc.utilisateur.siteListLocaux;

  closeDialog = (): void => this.displayDialog.set(false);

  onChangeSite = (idSite: number): void => {
    this.formData.update(s => ({...s, site: {id: idSite} as SiteDTO}));
  };

  onChangeTaxe = ($event: any): void => {
    const selected = $event.selectedItem as TaxeDTO;
    this.formData.update(s => ({...s, taxe: selected ?? null}));
  };

  onChangeLibelle = ($event: any): void => {
    this.formData.update(s => ({...s, libelle: $event.value}));
  };

  onChangeCode = ($event: any): void => {
    this.formData.update(s => ({...s, code: $event.value}));
  };

  onChangeTypeFraisFixe = ($event: any): void => {
    this.formData.update(s => ({...s, typeFraisFixe: $event.value}));
  };

  customTaxeDisplayExpr = (taxe: any): string => taxe && `${taxe.libelle} - ${taxe.valeur}%`;

  save = (): void => {
    if (!this.isFormValid()) {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, 'Veuillez compléter le formulaire');
      return;
    }
    const payload = this.formData() as FraisFixeDTO;
    const isUpdate: boolean = !!payload.id && payload.id !== 0;

    this.fraisFixeSvc.save(payload).subscribe(response => {
      if (response && response.one) {
        this.saved.emit({dto: response.one, isUpdate});
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, 'Sauvegarde du frais fixe réalisée avec succès');
        this.closeDialog();
      }
    });
  };
}
